<script setup lang="ts">
import {
  STATE_MENU_ADDRESS_SYSTEM,
  STATE_OF_MAP,
} from '@/constants/statesOfAddressSystem'

import {
  useAddressSystemMapStore,
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { BalloonType, PlacesSearch } from '@/store/delivery/delivery.types'
import { useMapSettingsStore } from '@/store/map-settings/map-settings'

import { DeliveryMethods } from '@/api/modules/basket/types'
import { TypeAddressSystemAreas } from '@/api/modules/delivery/types'
import { Success } from '@/api/results'
import { BasketPoint } from '@/types/api/api-v3/basket-points'
import { StateGeoAddress, TypeMaps } from '@/types/mapServices'

import AddressSystemMapTitle from '@/components/AddressSystem/map/AddressSystemMapTitle.vue'
import DeliveryMap from '@/components/Delivery/DeliveryMap.vue'

import type {
  DragCoordinates,
  LatLng,
  PointProperties,
} from '@/api/modules/delivery/types'

type Emits = {
  (fn: 'onClickRestaurant', restaurant: PointProperties | null): void
  (fn: 'onChangeCamera'): void
  (fn: 'onChangeVisibleBottomContent', value: boolean): void
  (fn: 'onChangeDialogModelValue'): void
  (fn: 'onClickMarker'): void
}

const emit = defineEmits<Emits>()

const FETCH_AREAS_FAILED = 'Не смогли загрузить адреса'

const { $apiService, $metrics, $sentryCaptureException } = useNuxtApp()

const { isMobile } = useResponsive()

const mapSettingsStore = useMapSettingsStore()
const { showSnackbar } = useAppStore()
const cityStore = useCityStore()

const {
  setDeliveryAddressState,
  resetDeliveryAddress,
  setDeliveryAddress,
  setSelectedAddress,
} = useAddressSystemStore()

const { changeMenuState } = useAddressSystemStateStore()
const { updateAreas, toggleLoadingAreas, setAreasData } =
  useAddressSystemMapStore()

const { deliveryAddress, deliveryBasket } = storeToRefs(useAddressSystemStore())
const { areas, isLoadingAreas, pickupPoints, hallPoints } = storeToRefs(
  useAddressSystemMapStore(),
)

const stateMap = ref(STATE_OF_MAP.loading)

const onChangeCamera = () => emit('onChangeCamera')

const isDeliveryCourier = computed(
  () => deliveryBasket.value.delivery_method === DeliveryMethods.COURIER,
)

// В случае если это mobile и выбран способ доставки курьер
const isVisibleMapComponent = computed(() => {
  return isMobile.value && isDeliveryCourier.value
})

const onClickToMap = async (newCoords: LatLng) => {
  // Для самовывоза/зала не ищем адреса
  if (!isDeliveryCourier.value) return

  let deliveryInfo = null

  toggleLoadingAreas() // лоадинг карты (суши)
  setDeliveryAddressState(StateGeoAddress.LOADING) // лоадинг балуна

  $metrics?.eventSendGoal({ ym: 'map_address_pin_moved' })

  const [lat, lon] = newCoords

  // Получаем информацию о точке
  const result = await $apiService.geocode.getAddressByCoordinates([lon, lat])

  const addressInfo =
    await $apiService.geocode.getAddressInfoByCoordinates(newCoords)

  if (!(result instanceof Success)) {
    setDeliveryAddressState(StateGeoAddress.READY)
    toggleLoadingAreas()
    resetDeliveryAddress(PlacesSearch.MAP)

    showSnackbar({ message: result.message })
    return
  }

  // Если информация о доставке есть
  if (addressInfo instanceof Success) deliveryInfo = addressInfo.data

  // Фиксируем место выбора и адрес
  const newDeliveryAddress: DeliveryDataAddress = {
    address: {
      address: result.data,
      place: PlacesSearch.MAP,
    },
    details: deliveryInfo,
    state: StateGeoAddress.READY,
    coordinates: newCoords,
  }

  setDeliveryAddress(newDeliveryAddress)

  if (!newDeliveryAddress.details) {
    changeMenuState(STATE_MENU_ADDRESS_SYSTEM.NOT_DELIVERY)
  }

  // Фиксируем строку адреса и координаты в autocomplete
  if (result.data.value) {
    setSelectedAddress({
      address: result.data.value,
      coordinates: newCoords,
    })
  }

  // В случае если сервис не полностью определить улицу и дом
  if (!result.data.street && !result.data.house && deliveryInfo) {
    $sentryCaptureException(
      `Не удалость определить адрес сервису адресов: ${result.data.value}`,
    )
  }

  // Убираем лоадинг карты и балуна
  toggleLoadingAreas()
  setDeliveryAddressState(StateGeoAddress.READY)
}

const onClickRestaurant = (restaurant: PointProperties | null) => {
  emit('onClickRestaurant', restaurant)
}

const onUpdateAreas = async (bounds: DragCoordinates) => {
  // Обноляем полигоны
  await updateAreas(bounds)
}

// Метод появления bottomSheet
const onChangeVisibleBottomContent = (value: boolean) => {
  emit('onChangeVisibleBottomContent', value)
}

const onChangeDialogModelValue = () => emit('onChangeDialogModelValue')

const onClickMarker = () => emit('onClickMarker')

const loadMap = async () => {
  stateMap.value = STATE_OF_MAP.loading

  // Загружаем настройки карты, если не загружали
  if (!mapSettingsStore.mapProviderApiKey) {
    const settings = await $apiService.city.fetchMapSettings()

    if (!(settings instanceof Success)) {
      stateMap.value = STATE_OF_MAP.error
      $metrics?.eventSendGoal({ ym: 'map_rendering_error' })

      return
    }

    mapSettingsStore.setMapSettings(settings.data)
  }

  // Отображаем карту сразу, полигоны подгружаем позже
  stateMap.value = STATE_OF_MAP.loaded
}

const initAreas = async () => {
  // Загружаем полигоны
  const resultAreas = await $apiService.delivery.fetchAreas(
    TypeAddressSystemAreas,
  )

  if (!(resultAreas instanceof Success)) {
    showSnackbar({ message: FETCH_AREAS_FAILED })

    return
  }

  setAreasData(resultAreas.data)
}

const balloonType = computed(() => {
  // TODO добавить определение типа в зависимости от значения таба
  return BalloonType.AddressSystem
})

const currentCity = computed((): string => {
  const { address } = deliveryAddress.value.address
  const { city, locality, area } = address || {}

  return city || locality || area || 'Не определено'
})

// Конвертируем type BasketPoint в PointProperties
// Т.к. модель точек в списке отличается от модели точек для карты
const convertPointProperty = (point: BasketPoint): PointProperties => {
  return {
    address: point.address,
    cafe: point.hall,
    cooking_time: 0, // Не хватает данных
    coordinates: [+point.coordinates.lat, +point.coordinates.lon],
    how_to_find: '', // Не хвататет данных
    is_open: point.is_open,
    slug: '', // Не хватает данных
    tags: [], // Не хватает данных
    working_hours: [], // Другие данные
    id: point.id,
    allow_redirect: point.allow_redirect, // На бэке в PointProperties моделе нет данных
    city_domain_prefix: point.city_domain_prefix, // На бэке в PointProperties моделе нет данных
  }
}

const pointsList = computed(() => {
  if (deliveryBasket.value.delivery_method === DeliveryMethods.PICKUP) {
    return pickupPoints.value.map(convertPointProperty)
  }

  if (deliveryBasket.value.delivery_method === DeliveryMethods.HALL) {
    return hallPoints.value.map(convertPointProperty)
  }

  // При "Доставка" отдаем пустой массив
  return []
})

// Выбранный ресторан из списка, чтобы приблизить его на карте
const selectedRestaurant = ref<PointProperties | null>(null)

watch(
  () => deliveryBasket.value,
  (newValue) => {
    if (!newValue.point_id) return (selectedRestaurant.value = null)

    selectedRestaurant.value =
      pointsList.value.find(
        (point) => point.id === deliveryBasket.value.point_id,
      ) || null
  },
)
onMounted(() => {
  initAreas()
  loadMap()
})

onBeforeUnmount(() => $metrics?.eventSendGoal({ ym: 'map_window_closed' }))
</script>

<template>
  <div class="address-system-map">
    <the-loader v-if="stateMap === STATE_OF_MAP.loading" />

    <delivery-map
      v-if="stateMap === STATE_OF_MAP.loaded"
      class="address-system-map__component"
      position-control="right"
      :selected-restaurant="selectedRestaurant"
      :points="pointsList"
      :delivery-address="deliveryAddress"
      :areas="areas"
      :is-loading-areas="isLoadingAreas"
      :is-delivery-courier="isDeliveryCourier"
      :balloon-type="balloonType"
      :component-map="TypeMaps.ADDRESS_SYSTEM"
      @on-click-to-map="onClickToMap"
      @on-click-marker="onClickMarker"
      @on-click-restaurant="onClickRestaurant"
      @on-update-center-map="onChangeCamera"
      @on-update-areas="onUpdateAreas"
    />

    <the-button
      v-if="isMobile"
      variant="transparent"
      icon="close-bigger"
      size="sm"
      radius="lg"
      class="address-system-map__button-close"
      @click="onChangeDialogModelValue"
    />

    <address-system-map-city
      v-if="isVisibleMapComponent"
      class="address-system-map__button-city"
      :current-city="currentCity"
      :city-visible-name="cityStore.cityInfo.visible_name"
    />

    <address-system-map-title
      v-if="isVisibleMapComponent"
      :address="deliveryAddress.address.address"
      class="address-system-map__title-address"
    />

    <address-system-map-actions
      v-if="isMobile"
      class="address-system-map__actions"
      :is-check-delivery-details="!!deliveryAddress.details"
      @on-change-visible-bottom-content="onChangeVisibleBottomContent"
    />

    <address-system-dialog-error
      v-if="stateMap === STATE_OF_MAP.error"
      is-map
      @on-update-method="loadMap"
    />
  </div>
</template>

<style scoped lang="scss">
.address-system-map {
  width: 100%;

  &__button-city {
    position: fixed;
    top: 10px;
    left: 10px;
  }

  &__button-close {
    position: fixed;
    top: 10px;
    right: 10px;
    background-color: var(--ui-white);
    box-shadow: var(--ui-card-shadow);
  }

  &__title-address {
    position: fixed;
    top: 58px;
    left: 0;
  }

  &__actions {
    position: fixed;
    bottom: 1.2%;
    left: 50%;
    z-index: 11;
    width: 90%;
    transform: translate(-50%, -40%);
  }

  &__component {
    height: inherit;
  }
}
</style>
